// Tap the region: a lead on our own 12-lead paper ("Tap a lead showing ST elevation") or a reading on the bedside
// monitor. A tap locks and sends { optionId } with the region's id.
// At the reveal every right region is ringed in green (there can be more than one), a wrong pick in red, and each
// region shows the room's share. In classroom mode the graphic sits small above one big button per region.
import { useMemo } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import type { EcgGraphic, ObsGraphic, PublicItem, Reveal } from '@shared/types';
import { Stem } from '@/components/Stem';
import { LeadPicker, type LeadPick } from '@/clinical/ecg/LeadPicker';
import { EcgPaperCard } from '@/clinical/ecg/EcgPaper';
import { ObsMonitor, type ObsParam, type ObsPick } from '@/clinical/ObsMonitor';
import { monitorRhythm } from '@/clinical/monitor/rhythm';
import type { Lead } from '@/clinical/ecg/types';
import type { FormatViewProps } from './types';
import { answerStateAny, effectivePhase, type AnswerState } from './layout';
import { OptionList, pickOf, useAnswerTap, TickGlyph, CrossGlyph } from './parts';
import { isRightPick, optionPct, rightIds } from './credit';
import { LEAD_PAPER_ORDER, OBS_IDS, OBS_NAME, listNames, regionsOf, type ObsId, type Region } from './hotspotMap';
import { Split, useWide } from './split';

export function HotspotView(props: FormatViewProps) {
  const { question: q, reveal, display = 'phones' } = props;
  const mine = pickOf(props);
  const phase = effectivePhase(props.phase, mine, reveal);
  const { tap, live } = useAnswerTap({ ...props, phase });
  const map = useMemo(() => regionsOf(q), [q]);
  const revealed = phase === 'revealed' && reveal ? reveal : undefined;
  const right = useMemo(() => (revealed ? rightIds(revealed) : []), [revealed]);
  const stateOf = (id: string): AnswerState => answerStateAny(id, phase, mine, revealed, right);
  const pctOf = revealed ? (id: string) => optionPct(q, revealed, id) : undefined;
  const screen = display === 'screen';
  const ecg = map.target === 'ecg-lead' && q.graphic?.type === 'ecg' ? q.graphic : undefined;
  const obs = map.target === 'obs-param' && q.graphic?.type === 'obs' ? q.graphic : undefined;
  const drawable = map.regions.length > 0 && (ecg || obs);
  const rightNames = revealed ? map.regions.filter((r) => right.includes(r.id)).map((r) => regionName(r, Boolean(ecg))) : [];
  const mineRegion = mine ? map.regions.find((r) => r.id === mine) : undefined;
  const wide = useWide(display);

  // Nothing to draw the regions on: every option is a plain answer (every right one marked at the reveal).
  if (!drawable) {
    return (
      <div className="fx-format" data-format="hotspot" data-phase={phase} data-display={display} data-layout={wide ? 'split' : undefined}>
        <Split wide={wide}
          left={!screen && <Stem text={q.stem} />}
          right={<OptionList options={q.options} phase={phase} myOptionId={mine} reveal={revealed} right={right} live={live} onTap={tap} layout="rows" />} />
      </div>
    );
  }

  // On a desktop the trace or monitor is the control, so it stays large on the left; the extra answers and, at the
  // reveal, the answer named in words go on the right.
  const extra = map.extra.length > 0
    ? <OptionList options={map.extra} phase={phase} myOptionId={mine} reveal={revealed} right={right} live={live} onTap={tap} layout="rows" />
    : null;
  const named = revealed && rightNames.length > 0 ? (
    <p className="fx-hot-answer">
      <span className="fx-hot-answer-cap">{rightNames.length === 1 ? 'The answer' : 'The answers'}</span>
      {sentenceCase(listNames(rightNames))}
      {mineRegion && !isRightPick(mine, revealed) && <span className="fx-hot-answer-you">You tapped {regionName(mineRegion, Boolean(ecg))}</span>}
    </p>
  ) : null;
  const split = wide && Boolean(extra || named);
  return (
    <div className="fx-format" data-format="hotspot" data-target={map.target} data-phase={phase} data-display={display} data-layout={split ? 'split' : undefined}>
      <Split wide={split} left={<>
      {!screen && <Stem text={q.stem} />}
      {screen ? (
        <>
          <p className="sr-only">The question is on the big screen. Answer here.</p>
          <div className="fx-hot-mini">
            {ecg
              ? <EcgPaperCard graphic={ecg} revealed={Boolean(revealed)} decorative
                  pick={{ regions: map.regions.map((r) => ({ id: r.id, lead: r.key as Lead })), stateOf, disabled: true, mine } satisfies LeadPick} />
              : <ObsMonitor graphic={obs!} revealed={Boolean(revealed)} rhythm={monitorRhythm(obs!, q.stem)} />}
          </div>
          <RegionButtons regions={orderRegions(map.regions, ecg ? 'ecg' : 'obs')} columns={ecg ? 4 : 3} stateOf={stateOf} pctOf={pctOf} mine={mine} live={live} onTap={tap} revealed={Boolean(revealed)} />
        </>
      ) : ecg ? (
        <LeadPicker graphic={ecg as EcgGraphic} revealed={Boolean(revealed)} title={ecg.caption}
          pick={{ regions: map.regions.map((r) => ({ id: r.id, lead: r.key as Lead })), stateOf, disabled: !live, onPick: tap, pctOf, mine } satisfies LeadPick} />
      ) : (
        <ObsMonitor graphic={obs as ObsGraphic} revealed={Boolean(revealed)} rhythm={monitorRhythm(obs!, q.stem)}
          pick={{ regions: map.regions.map((r) => ({ id: r.id, param: r.key as ObsParam })), stateOf, disabled: !live, onPick: tap, pctOf, mine } satisfies ObsPick} />
      )}
      </>} right={(extra || named) && <>{extra}{named}</>} />
    </div>
  );
}

const sentenceCase = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
/** A region in a sentence: the lead as printed ("V2"), or the observation's name ("respiratory rate"). */
function regionName(r: Region, lead: boolean): string {
  return lead ? r.short : (OBS_NAME[r.key as ObsId] ?? r.label).toLowerCase();
}

/**
 * The graphic with every right region marked and the room's share on each, without a player: for the stage, which shows
 * the answer to the room. Nothing on it can be tapped.
 */
export function HotspotAnswer({ question: q, reveal }: { question: Pick<PublicItem, 'hotspot' | 'graphic' | 'options' | 'format' | 'multi' | 'stem'>; reveal: Reveal }) {
  const map = useMemo(() => regionsOf(q), [q]);
  const right = rightIds(reveal);
  const stateOf = (id: string): AnswerState => (right.includes(id) ? 'correct' : 'dim');
  const pctOf = (id: string) => optionPct(q, reveal, id);
  if (map.target === 'ecg-lead' && q.graphic?.type === 'ecg') {
    return <LeadPicker graphic={q.graphic} revealed title={q.graphic.caption} pick={{ regions: map.regions.map((r) => ({ id: r.id, lead: r.key as Lead })), stateOf, disabled: true, pctOf }} />;
  }
  if (map.target === 'obs-param' && q.graphic?.type === 'obs') {
    return <ObsMonitor graphic={q.graphic} revealed rhythm={monitorRhythm(q.graphic, q.stem)} pick={{ regions: map.regions.map((r) => ({ id: r.id, param: r.key as ObsParam })), stateOf, disabled: true, pctOf }} />;
  }
  return null;
}

/** Classroom buttons follow the graphic: the 12-lead read across as printed, the monitor's readings top to bottom. */
function orderRegions(regions: readonly Region[], kind: 'ecg' | 'obs'): Region[] {
  const order: readonly string[] = kind === 'ecg' ? LEAD_PAPER_ORDER : OBS_IDS;
  return [...regions].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
}

/** One big button per region (classroom mode), with right and wrong and the room's share at the reveal. */
export function RegionButtons({ regions, columns, stateOf, pctOf, mine, live, onTap, revealed }: {
  regions: readonly Region[]; columns: number; stateOf: (id: string) => AnswerState; pctOf?: (id: string) => number;
  mine?: string; live: boolean; onTap: (id: string) => void; revealed: boolean;
}) {
  const reduce = useReducedMotion() ?? false;
  return (
    <div className="fx-regions" role="group" aria-label="Regions" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {regions.map((r, i) => {
        const st = stateOf(r.id);
        const share = pctOf?.(r.id);
        const isMine = r.id === mine;
        return (
          <button key={r.id} type="button" className="fx-region" data-option={r.id} data-state={st} data-mine={isMine ? '' : undefined}
            aria-pressed={st === 'locked' || (isMine && revealed)} disabled={!live} onClick={() => onTap(r.id)}
            aria-label={`${r.label}.${revealed && st === 'correct' ? ' A right answer.' : ''}${isMine ? (revealed ? ' Your pick.' : ' Locked in.') : ''}${share !== undefined ? ` ${share}% of the room.` : ''}`}>
            {chemShort(r.short)}
            {revealed && (st === 'correct' || st === 'wrong') && (
              <span className="fx-region-mark" data-tone={st}>{st === 'correct' ? <TickGlyph size={14} stroke={3.6} /> : <CrossGlyph size={14} stroke={3.6} />}</span>
            )}
            {revealed && isMine && <span className="fx-you" data-tone={st}>You</span>}
            {share !== undefined && (
              <motion.small initial={reduce ? { opacity: 0 } : { opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.04 }}>{share}%</motion.small>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** SpO2 and O2 with the 2 set low, as on the monitor. */
function chemShort(s: string) {
  const m = /^(.*O)2$/.exec(s);
  return m ? <span>{m[1]}<sub className="fx-sub">2</sub></span> : <span>{s}</span>;
}
